import React from 'react';
import { HealthRuleId, InventoryHealthStats } from '../../../../types/inventoryHealth';
import { HealthOwnerFilter } from './HealthOwnerFilter';
import { Search, Download, RefreshCw } from 'lucide-react';

interface HealthRuleFilterBarProps {
  selectedRule: HealthRuleId | 'all' | 'healthy_only' | 'critical_only' | 'warning_only';
  onSelectRule: (rule: HealthRuleId | 'all' | 'healthy_only' | 'critical_only' | 'warning_only') => void;
  search: string;
  onSearchChange: (s: string) => void;
  stats: InventoryHealthStats;
  owners: string[];
  selectedOwner: string;
  onSelectOwner: (owner: string) => void;
  onRefresh: () => void;
  onExportCsv: () => void;
}

export const HealthRuleFilterBar: React.FC<HealthRuleFilterBarProps> = ({
  selectedRule,
  onSelectRule,
  search,
  onSearchChange,
  stats,
  owners,
  selectedOwner,
  onSelectOwner,
  onRefresh,
  onExportCsv,
}) => {
  const chips: Array<{ id: typeof selectedRule; label: string; count?: number; dotColor?: string }> = [
    { id: 'all', label: 'Todos', count: stats.totalActive },
    { id: 'critical_only', label: 'Críticos', count: stats.criticalCount, dotColor: 'bg-rose-500' },
    { id: 'warning_only', label: 'Advertencias', count: stats.warningCount, dotColor: 'bg-amber-500' },
    { id: 'healthy_only', label: '100% Sanos', count: stats.healthyCount, dotColor: 'bg-emerald-500' },
    { id: 'admin_fee', label: '1. Admón < $100k', count: stats.ruleViolationsCount.admin_fee },
    { id: 'floor_number', label: '2. Sin Piso', count: stats.ruleViolationsCount.floor_number },
    { id: 'elevator_field', label: '3. Sin Ascensor', count: stats.ruleViolationsCount.elevator_field },
    { id: 'geo_city_mismatch', label: '4. Error Coords/Ciudad', count: stats.ruleViolationsCount.geo_city_mismatch },
    { id: 'high_floor_no_elevator', label: '5. Piso ≥ 5 sin ascensor', count: stats.ruleViolationsCount.high_floor_no_elevator },
    { id: 'price_out_of_range', label: '6. Precio < $35M', count: stats.ruleViolationsCount.price_out_of_range },
    { id: 'invalid_area', label: '7. Área 0 m²', count: stats.ruleViolationsCount.invalid_area },
    { id: 'missing_rooms_baths', label: '8. Hab/Baños', count: stats.ruleViolationsCount.missing_rooms_baths },
    { id: 'insufficient_photos', label: '9. < 5 Fotos', count: stats.ruleViolationsCount.insufficient_photos },
    { id: 'missing_estrato', label: '10. Sin Estrato', count: stats.ruleViolationsCount.missing_estrato },
    { id: 'zero_antiguedad', label: '11. Antigüedad 0', count: stats.ruleViolationsCount.zero_antiguedad, dotColor: 'bg-orange-500' },
  ];

  return (
    <div className="space-y-3 mb-5">
      <div className="flex flex-col lg:flex-row gap-2 justify-between items-stretch lg:items-center">
        <div className="flex flex-col sm:flex-row gap-2 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
            <input
              id="health-search-input"
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Buscar por código, BRP, dirección, dueño..."
              className="w-full pl-9 pr-4 py-2 text-xs font-semibold bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none transition-all"
            />
          </div>
          <HealthOwnerFilter
            owners={owners}
            selectedOwner={selectedOwner}
            onSelectOwner={onSelectOwner}
          />
        </div>

        <div className="flex items-center gap-2 justify-end">
          <button
            id="btn-health-refresh"
            onClick={onRefresh}
            title="Refrescar auditoría"
            className="p-2 text-gray-500 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            id="btn-health-export"
            onClick={onExportCsv}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-black uppercase tracking-wider bg-sky-50 text-sky-700 hover:bg-sky-100 border border-sky-200 rounded-xl transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            <span>Exportar CSV</span>
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1.5 overflow-x-auto pb-1.5 custom-scrollbar">
        {chips.map((chip) => {
          const isActive = selectedRule === chip.id;
          return (
            <button
              key={chip.id}
              id={`filter-chip-${chip.id}`}
              onClick={() => onSelectRule(chip.id)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                isActive
                  ? 'bg-gray-900 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {chip.dotColor && <span className={`w-1.5 h-1.5 rounded-full ${chip.dotColor}`} />}
              <span>{chip.label}</span>
              {chip.count !== undefined && (
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-black ${
                  isActive ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700'
                }`}>
                  {chip.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
